import {produce} from 'immer';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';

import {Metric, MetricsRequest, MetricsResultsMap} from 'app_api/projects/metrics/types';
import {ControlBarAction, ControlBarActionItems} from 'common/query-builder/query-entry-section/constants';
import {
  Action,
  Calculation,
  EntryPart,
  FormulaItem,
  isFormulaEntry,
  MetricEntry,
  QueryEventEntry,
  QueryFormulaEntry,
  QueryMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {getQueryEntryUpdateFromCalculation} from 'common/query-builder/query-entry-section/util';
import {FeatureGate, ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {createMetric, updateMetric} from 'common/report/queries/app-api/metrics';
import {getProjectId} from 'common/report/util';
import {KEY_VALUES} from 'common/util/dom';
import {Model, ModelStatus} from 'common/util/model-resource/model-resource';
import {createEntityModalPromise} from 'common/widgets/create-entity-modal';
import {FormulaMetricId} from 'common/query-builder/query-block/formulas-menu/types';
import {getEntries, getShowSection} from 'common/report/insights/util/events';
import {FormulaShowClause} from 'common/report/insights/util/types';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {getAlphaLabelFromIndex} from 'common/util';
import {getResolvedFormulaEntry, isModifiedShowClauseFormula} from 'common/query-builder/query-builder/util';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';
import {buildCreateEntityModalAttrs} from 'common/report/util/saved-entities';

import {BaseEntryRow} from '../base';
import {FormulaEntryRowEl} from './template';
import {
  FormulaParseError,
  getMetricUpdateFromFormulaShowClause,
  getNextFormulaName,
  getUpdatedFormulaAndMetrics,
  parseFormula,
} from './utils';

const canSaveMetrics = (section: QueryEntrySection) =>
  section.isConnected &&
  section.getContext(`currentProject`).get()?.value?.hasFeatureFlag(ProjectFeatureFlag.SavedFormulas);

function getNewInlineEntry(section: QueryEntrySection): FormulaItem {
  const existingFormulaNames = section
    .getEntries()
    .filter((entry): entry is QueryFormulaEntry => {
      return (
        isFormulaEntry(entry) &&
        // if it's a placeholder formula entry, it won't have event yet
        Boolean(entry.event)
      );
    })
    .map((entry) => entry.event.name)
    .filter(Boolean);

  return {
    name: getNextFormulaName({existingFormulaNames}),
    definition: ``,
  };
}

export class FormulaEntryRow extends BaseEntryRow<QueryFormulaEntry> {
  template = FormulaEntryRowEl;

  constructor(args) {
    super(args);
  }

  get canSaveMetrics() {
    return canSaveMetrics(this.section);
  }

  getResolvedFormulaShowClause(): FormulaShowClause | null {
    if (this.section.isConnected === false) {
      return null;
    }
    const resolvedEntry = getResolvedFormulaEntry({
      metricResource: this.section.getContext(`metrics`),
      formulaEntry: this.entry,
    });

    return (
      resolvedEntry && {
        formula: resolvedEntry.event,
        measurement: {
          cumulative: resolvedEntry.cumulative,
          rolling: resolvedEntry.rolling,
        },
      }
    );
  }

  getName(): string | null {
    return this.getResolvedFormulaShowClause()?.formula.name;
  }

  getDefinition(): string | null {
    return this.getResolvedFormulaShowClause()?.formula.definition;
  }

  get model(): Model<Metric> {
    if (this.entry.event?.id) {
      if (this.section.isConnected) {
        return this.section.getContext(`metrics`).getOne(this.entry.event.id);
      } else {
        return {status: ModelStatus.Loading};
      }
    } else {
      return null;
    }
  }

  get isLoadingModel() {
    return this.model?.status === ModelStatus.Loading;
  }

  getFormulaError() {
    return this.getFormulaValidationError() || this.entry.event?.error;
  }

  getReferencedEntries(): Array<QueryMetricEntry | MetricEntry> {
    const referencedMetrics = this.getResolvedFormulaShowClause()?.formula.referencedMetrics;
    if (referencedMetrics) {
      return getEntries({showClauses: referencedMetrics});
    }
    return null;
  }

  actionHandlers = {
    [ControlBarAction.Save]: () => this.saveFormula(),
    [ControlBarAction.RollingAverage]: (value: TODO) => this.handleCalculationSelected(value.calculation),
    [ControlBarAction.Cumulative]: (value: TODO) => this.handleCalculationSelected(value.calculation),
  };

  getFormulaValidationError(): string | null {
    if (this.isLoadingModel) {
      return null;
    }

    const formulaDefinition = this.getDefinition();

    if (formulaDefinition) {
      try {
        const entries = this.getReferencedEntries() || this.section.getEntries();
        const variables = entries
          .map((entry, idx) => (!isFormulaEntry(entry) ? getAlphaLabelFromIndex(idx) : null))
          .filter(Boolean);

        parseFormula(formulaDefinition, variables);
      } catch (err) {
        if (err instanceof FormulaParseError) {
          return err.message;
        }

        // we can ignore non-parser errors as to not interrupt the UX. the formula will be validated again at query
        console.error(err);
      }
    }

    return null;
  }

  isSelectingFormula(): boolean {
    return this.entry.event === null;
  }

  isUnsavedFormula(): boolean {
    return isNil(this.entry.event?.id);
  }

  isSavedFormula() {
    return this.isUnsavedFormula() === false;
  }

  isPlaceholder(): boolean {
    return (
      this.entry.event === null ||
      this.getDefinition() === `` ||
      (this.getDefinition() && this.getFormulaValidationError() !== null)
    );
  }

  overflowControls() {
    return [
      // display the save action for inline formulas or
      // for saved formulas with modifications
      this.canSaveMetrics && this.isPlaceholder() === false && ControlBarActionItems[ControlBarAction.Save],

      // hide action only for insights
      this.section.attr(`enable-hide-entry`) &&
        this.isPlaceholder() === false &&
        ControlBarActionItems[this.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],

      // calculation options
      ControlBarActionItems[ControlBarAction.RollingAverage]({
        currentRollingWindowSize: this.entry.rolling,
        dateUnit: this.section.attr(`date-unit`),
      }),
      ControlBarActionItems[ControlBarAction.Cumulative],
    ].filter(Boolean);
  }

  handleUpsertedFormula(results: MetricsResultsMap): void {
    if (!results) {
      return;
    }

    const metric = Object.values(results)[0];
    this.section.getContext(`metrics`).update(results, {partialUpdate: false});

    const entry: QueryFormulaEntry = {
      type: ResourceType.Formula,
      event: {id: metric.id},
      isExpanded: false,
    };

    this.section.updateEntries(Action.Edit, {
      index: this.index,
      entry,
    });
  }

  async saveFormula(isDuplicating = false) {
    if (this.isModifiedFormula() && isDuplicating === false) {
      return this.handleSaveModifications();
    }

    let {name, definition, referencedMetrics} = this.entry.event;

    /**
     * when saving an inline formula, we take reference metrics
     * from the main report, when duplicating a formula we want
     * to duplicate data within the formula definition
     */
    if (this.isModifiedFormula() === false) {
      const updatedDefinition = getUpdatedFormulaAndMetrics(
        {name, definition},
        this.section.getEntries() as Array<QueryMetricEntry | MetricEntry>,
      );

      referencedMetrics = updatedDefinition.referencedMetrics;
      definition = updatedDefinition.definition;
    }

    const metricEntity: MetricsRequest = {
      type: `formula`,
      name,
      description: ``,
      definition: {
        formula: {
          definition,
          referencedMetrics,
        },
        measurement: this.getResolvedFormulaShowClause().measurement,
      },
    };

    const result = await createEntityModalPromise(
      buildCreateEntityModalAttrs({
        projectId: parseInt(getProjectId(), 10),
        entityType: EntityType.Metric,
        modalOpen: true,
        isEditing: false,
        isDuplicating,
        entity: metricEntity,
        showShareManagement: this.section
          .getContext(`currentProject`)
          .get()
          ?.value?.getFeatureGate(FeatureGate.Sharing),
      }),
      {
        existingEntityNames: this.section
          .getContext(`metrics`)
          .getAllValues()
          .map((value) => value.name),
      },
    );

    if (result.didSave) {
      const results = await createMetric({
        ...metricEntity,
        name: result.name,
        description: result.description,
      });

      this.handleUpsertedFormula(results);

      if (result.didShare) {
        await promptSharing({
          type: SharableEntityType.Metric,
          id: Number(Object.keys(results)[0]),
          'project-id': Number(getProjectId()),
        });
      }
    }
  }

  static getNewEntry(section: QueryEntrySection): QueryFormulaEntry {
    return {
      type: ResourceType.Formula,
      event: canSaveMetrics(section) ? null : getNewInlineEntry(section),
      filters: [],
    };
  }

  /**
   * Turns this entry into an empty inline entry.
   */
  createNewInlineEntry() {
    this.section.updateEntries(Action.Edit, {
      index: this.index,
      entry: {
        type: ResourceType.Formula,
        event: getNewInlineEntry(this.section),
      },
    });
  }

  onDefinitionCommit(newDefinition: string) {
    if (newDefinition) {
      this.updateFormula({definition: newDefinition});
    } else if (!this.section.isStagedEntry(this.index)) {
      // remove the formula entry if its definition is edited to be the empty string.
      // the !isStagedEntry check is to limit this codepath to editing existing definition rather than newly
      // staged formula.
      this.section.updateEntries(Action.Remove, {index: this.index});
    }
    this.section.helpers.setActiveEntry(`close`, {index: this.index, part: EntryPart.Value});
  }

  handleSelect(id: FormulaMetricId) {
    const entry: QueryFormulaEntry = {
      type: ResourceType.Formula,
      event: {id},
    };

    this.section.updateEntries(Action.Edit, {
      index: this.index,
      entry,
    });
  }

  handleDefinitionKeydown(ev: KeyboardEvent) {
    if (ev.key === KEY_VALUES.ENTER) {
      ev.preventDefault();
      const inputEl = ev.currentTarget as HTMLInputElement;
      inputEl.blur();
    }
  }

  handleDefinitionInput(ev: InputEvent) {
    const inputEl = ev.currentTarget as HTMLInputElement;
    inputEl.value = inputEl.value.toUpperCase();
  }

  handleDefinitionBlur(ev: FocusEvent) {
    this.onDefinitionCommit((ev.currentTarget as HTMLInputElement).value);
  }

  handleDefinitionFocus() {
    if (this.isSavedFormula()) {
      return;
    }

    const {index: activeIndex, part: activePart} = this.section.state.openEntry;
    if (activeIndex === this.index && activePart === EntryPart.Value) {
      // The formula will already be openEntry if it was added as new formula and autofocused. In this
      // case, don't update to avoid an unnecessary re-render
      return;
    }
    this.section.helpers.setActiveEntry(`open`, {index: this.index, part: EntryPart.Value});
  }

  handleCalculationRemoved(): void {
    this.updateFormula({rolling: null, cumulative: null});
  }

  handleCalculationSelected(calculation: Calculation): void {
    this.updateFormula(getQueryEntryUpdateFromCalculation({originalEntry: this.entry, calculation}));
  }

  onReferencedMetricsUpdate(ev: CustomEvent<{entries: QueryEventEntry[]}>) {
    this.updateFormula({
      referencedMetrics: getShowSection({entries: ev.detail.entries}).toBookmarkData(),
    });
  }

  updateFormula(
    modifiedParams: Partial<
      Pick<FormulaItem, 'name' | 'definition' | 'referencedMetrics'> & Pick<QueryFormulaEntry, 'rolling' | 'cumulative'>
    >,
  ) {
    const {isExpanded} = this.entry;
    const resolvedFormulaClause = this.getResolvedFormulaShowClause();
    const isModifyingSavedFormula = Boolean(this.entry.event.id) && !isExpanded;

    this.section.updateEntries(Action.Edit, {
      entry: produce(this.entry, (draft) => {
        draft.event = {
          ...resolvedFormulaClause.formula,
          ...pick(modifiedParams, [`name`, `definition`, `referencedMetrics`]),
        };
        Object.assign(draft, {
          ...resolvedFormulaClause.measurement,
          ...pick(modifiedParams, [`rolling`, `cumulative`]),
        });

        if (isModifyingSavedFormula) {
          draft.isExpanded = true;
        }
      }),
      index: this.index,
    });
  }

  /**
   * Returns true when a saved formula has been edited
   */
  isModifiedFormula(): boolean {
    return isModifiedShowClauseFormula(this.entry.event);
  }

  handleResetModifications(): void {
    const savedFormula = this.model.value;
    this.section.updateEntries(Action.Edit, {
      entry: produce(this.entry, (draft) => {
        const updates: Partial<QueryFormulaEntry> = {
          event: {
            // removing all fields except id will make this an unmodified saved formula
            id: savedFormula.id,
          },
          rolling: null,
          cumulative: null,
          isExpanded: false,
        };
        Object.assign(draft, updates);
      }),
      index: this.index,
    });
  }

  async handleSaveModifications(saveAsNew = false): Promise<void> {
    if (saveAsNew) {
      return this.saveFormula(saveAsNew);
    }

    const results = await updateMetric({
      metricId: this.model.value.id.toString(),
      metric: getMetricUpdateFromFormulaShowClause(this.getResolvedFormulaShowClause()),
    });
    this.handleUpsertedFormula(results);
  }
}
